import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { 
  Brain, FileSearch, Loader2, AlertTriangle, 
  CheckCircle2, RefreshCw, Lightbulb, TrendingUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AnalysisPanel Component
 * 
 * Operator tool for running analysis steps (M4/M5).
 * Only shown during ANALYSIS_SYNTHESIS state.
 * 
 * Props:
 * - serId: Service Execution Record ID
 * - onAnalysisUpdate: Callback when analysis is updated
 */
const AnalysisPanel = ({ serId, onAnalysisUpdate }) => {
  const { token } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningSignals, setRunningSignals] = useState(false);
  const [runningAssumptions, setRunningAssumptions] = useState(false);

  const fetchPreview = async () => {
    if (!serId || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/engagements/${serId}/analysis/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.error?.message || 'Failed to fetch analysis preview');
      }
      
      const data = await response.json();
      setPreview(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load analysis preview:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, [serId, token]);

  const handleRunSignalExtraction = async () => {
    setRunningSignals(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/analysis/run-signal-extraction`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.error?.message || 'Failed to run signal extraction');
      }
      
      const data = await response.json();
      
      if (data.milestone_completed) {
        toast.success(`Signal extraction complete! ${data.signal_count} signals extracted. M4 milestone completed.`);
      } else {
        toast.success(`Signal extraction complete! ${data.signal_count} signals extracted.`);
      }
      
      // Refresh preview
      await fetchPreview();
      onAnalysisUpdate?.();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunningSignals(false);
    }
  };

  const handleRunAssumptions = async () => {
    setRunningAssumptions(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/analysis/run-assumptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.error?.message || 'Failed to generate assumptions');
      }
      
      const data = await response.json();
      
      if (data.assumptions_not_required) {
        toast.success("Assumptions documented: Context sufficient - no assumptions required. M5 milestone completed.");
      } else if (data.milestone_completed) {
        toast.success(`Assumptions generated! ${data.assumption_count} assumptions documented. M5 milestone completed.`);
      } else {
        toast.success(`Assumptions generated! ${data.assumption_count} assumptions documented.`);
      }
      
      // Refresh preview
      await fetchPreview();
      onAnalysisUpdate?.();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunningAssumptions(false);
    }
  };

  if (loading) {
    return (
      <div className="glass p-6 rounded-xl" data-testid="analysis-panel">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-slate-400">Loading analysis status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass p-6 rounded-xl" data-testid="analysis-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-purple-400" />
          <div>
            <h3 className="font-semibold text-white">Analysis Synthesis</h3>
            <p className="text-sm text-slate-400">
              Extract signals and document assumptions
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchPreview}
          className="text-slate-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* M4: Signals */}
        <div className={`rounded-lg p-4 border ${
          preview?.m4_complete 
            ? 'bg-green-950/30 border-green-500/30' 
            : 'bg-slate-800/50 border-slate-700'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <FileSearch className={`w-4 h-4 ${preview?.m4_complete ? 'text-green-400' : 'text-purple-400'}`} />
            <span className="text-sm font-medium text-white">Signal Extraction</span>
            {preview?.m4_complete && <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />}
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {preview?.signals_summary?.signal_count || 0}
          </div>
          <div className="text-xs text-slate-400">signals extracted</div>
          {preview?.signals_summary?.confidence_distribution && (
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">
                H: {preview.signals_summary.confidence_distribution.high || 0}
              </span>
              <span className="text-amber-400">
                M: {preview.signals_summary.confidence_distribution.medium || 0}
              </span>
              <span className="text-red-400">
                L: {preview.signals_summary.confidence_distribution.low || 0}
              </span>
            </div>
          )}
        </div>

        {/* M5: Assumptions */}
        <div className={`rounded-lg p-4 border ${
          preview?.m5_complete 
            ? 'bg-green-950/30 border-green-500/30' 
            : 'bg-slate-800/50 border-slate-700'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className={`w-4 h-4 ${preview?.m5_complete ? 'text-green-400' : 'text-amber-400'}`} />
            <span className="text-sm font-medium text-white">Assumptions</span>
            {preview?.m5_complete && <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />}
          </div>
          {preview?.assumptions_summary?.assumptions_not_required ? (
            <>
              <div className="text-lg font-bold text-green-400 mb-1">
                Not Required
              </div>
              <div className="text-xs text-slate-400">context sufficient</div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-white mb-1">
                {preview?.assumptions_summary?.assumption_count || 0}
              </div>
              <div className="text-xs text-slate-400">assumptions documented</div>
            </>
          )}
          {preview?.assumptions_summary?.severity_distribution && !preview?.assumptions_summary?.assumptions_not_required && (
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-red-400">
                High: {preview.assumptions_summary.severity_distribution.high || 0}
              </span>
              <span className="text-amber-400">
                Mod: {preview.assumptions_summary.severity_distribution.moderate || 0}
              </span>
              <span className="text-green-400">
                Low: {preview.assumptions_summary.severity_distribution.low || 0}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={handleRunSignalExtraction}
          disabled={runningSignals || preview?.m4_complete}
          className={`flex-1 ${
            preview?.m4_complete 
              ? 'bg-green-600/50 cursor-not-allowed' 
              : 'bg-purple-600 hover:bg-purple-700'
          }`}
          data-testid="run-signals-btn"
        >
          {runningSignals ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Extracting...
            </>
          ) : preview?.m4_complete ? (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Signals Complete
            </>
          ) : (
            <>
              <FileSearch className="w-4 h-4 mr-2" />
              Run Signal Extraction
            </>
          )}
        </Button>
        
        <Button
          onClick={handleRunAssumptions}
          disabled={runningAssumptions || preview?.m5_complete}
          className={`flex-1 ${
            preview?.m5_complete 
              ? 'bg-green-600/50 cursor-not-allowed' 
              : 'bg-amber-600 hover:bg-amber-700'
          }`}
          data-testid="run-assumptions-btn"
        >
          {runningAssumptions ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : preview?.m5_complete ? (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Assumptions Complete
            </>
          ) : (
            <>
              <Lightbulb className="w-4 h-4 mr-2" />
              Generate Assumptions
            </>
          )}
        </Button>
      </div>

      {/* Info note */}
      <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
        <p className="text-xs text-slate-400">
          <TrendingUp className="w-3 h-3 inline mr-1" />
          Signal extraction analyzes transcripts to identify operational patterns, risks, and opportunities.
          Assumptions are generated based on context completeness to ensure transparent analysis.
        </p>
      </div>
    </div>
  );
};

export default AnalysisPanel;
